import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IMG, SiteNav, SiteFooter } from "@/components/SiteChrome";
import { RootsLine } from "@/components/Roots";

const CONTACT = "lindsay@rootsmarketingco.uk";
const GET = [
  ["Your Brand & Marketing Foundations", "The bedrock of everything. Your mission (using the IKIGAI method), your Brand Positioning Statement, a deep profile of your ideal customer, your core transformation, what makes you different, your brand personality and tone of voice, and your content pillars. All written for your brand."],
  ["Your Mini Marketing Plan", "Built around the Roots Growth Model — a five-phase framework that maps the right marketing activity to your exact stage of business. Tells you what to prioritise, what to hold off on, and what belongs in your next 12 months. Practical enough to use as a brief for a designer or agency, a filter for every marketing decision, and a clear picture of exactly who you're targeting — and who you're not."],
  ["Your 30-Day Content Plan", "A full month of content ideas, mapped to your pillars and your audience — so you always know what to post and why."],
  ["A Clear, Structured Document", "Everything delivered in a clean PDF you can reference, share, and use."],
];
const HOW = [
  ["Fill in your questionnaire", "A detailed set of questions about your brand, your stage, your audience and your goals. Most founders take around 30–45 minutes."],
  ["We build your plan", "Your answers are run through The Roots Method — Lindsay's proprietary framework, built on 20 years of hands-on industry experience. Your plan is produced using a combination of Lindsay's strategic methodology and AI tools, with every output grounded in real industry knowledge, not generic advice."],
  ["Receive your plan", "Delivered as a PDF document within 2 working days."],
];
const QUOTES = [
  ["Wow Lindsay this is incredible!! Thanks so much. SO much info and a definite direction, the social media ideas are so good! Lots to think about, now I just need to do it!!!", "Kerrie Smith, Infinity Children's Therapy"],
  ["I just wanted to say thank you so much, this is superb. You are helping fill the space in my brain which cannot create or write whatever it is that's meant to be there. I read it and am like — yeah, this is what I need!", "Becky, I Do Handmade"],
];
const INCLUDED = ["Brand & Marketing Foundations", "Mini Marketing Plan (Roots Growth Model)", "30-Day Content Plan", "Clear, structured PDF document"];

const useMiniPlanPrice = () => {
  const { user } = useAuth();
  const [price, setPrice] = useState(user ? null : 99);
  useEffect(() => {
    if (user) api.get("/me/access").then((r) => setPrice(r.data.mini_plan_price)).catch(() => setPrice(99));
    else setPrice(99);
  }, [user]);
  return [user, price];
};

const BuyButtons = ({ testId, label }) => {
  const [user, price] = useMiniPlanPrice();
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = user ? await api.post("/payments/checkout", { origin_url: origin, item: "mini_plan" })
        : await api.post("/payments/mini-plan/guest", { origin_url: origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const member = user && price === 59;
  if (!price) return <div className="h-14" data-testid={`${testId}-loading`} />;
  return (
    <div className="flex flex-wrap items-center gap-4">
      <button onClick={buy} disabled={busy} data-testid={`${testId}-buy-${member ? "59" : "99"}`}
        className="inline-flex items-center gap-2 rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-8 py-4 text-lg font-medium transition-colors disabled:opacity-70">
        {busy && <Loader2 className="h-5 w-5 animate-spin" />}{member ? "Members: get yours for £59" : label}
      </button>
      {!user && <Link to="/login?next=/mini-marketing-plan" data-testid={`${testId}-member-login`} className="text-sm text-navy underline hover:text-pinkb">Members: get yours for £59 — log in</Link>}
    </div>
  );
};

const Hero = () => (
  <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28 grid gap-12 lg:grid-cols-[1.2fr_1fr] items-center" data-testid="mini-plan-hero">
    <div>
      <div className="eyebrow mb-4">The Roots Method</div>
      <h1 className="font-serif uppercase text-4xl sm:text-5xl lg:text-6xl text-navy leading-[0.95]">Your brand deserves a real marketing plan. Not a template. Yours.</h1>
      <p className="mt-6 text-lg text-foreground/80 max-w-xl">The Roots Method Mini Marketing Plan is a bespoke brand and marketing plan for food, drink and hospitality founders — built around your business, your stage, and what actually works.</p>
      <div className="mt-8"><BuyButtons testId="hero" label="Get Your Mini Marketing Plan — £99" /></div>
    </div>
    <img src={IMG.own1} alt="Food and drink founders at work" className="rounded-3xl aspect-[4/5] object-cover w-full" />
  </section>
);

const Pain = () => (
  <section className="bg-navy text-white">
    <div className="mx-auto max-w-4xl px-6 py-16 text-xl sm:text-2xl font-serif leading-snug" data-testid="mini-plan-pain">
      You're posting, pitching and spending money — and still not sure if any of it is working. Most of the advice out there wasn't built for your brand, your stage or your budget. <span className="text-sun">This was.</span>
    </div>
  </section>
);

const WhatYouGet = () => (
  <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20" data-testid="mini-plan-what">
    <h2 className="font-serif uppercase text-3xl text-navy mb-10">What you'll get</h2>
    <div className="grid gap-6 md:grid-cols-2">{GET.map(([t, b], i) => (
      <div key={t} className="rounded-3xl border border-border bg-card p-8">
        <div className="font-serif text-5xl text-pinkb">{i + 1}</div>
        <h3 className="font-serif uppercase text-xl text-navy mt-3">{t}</h3>
        <p className="mt-2 text-sm text-foreground/80 leading-relaxed">{b}</p>
      </div>
    ))}</div>
  </section>
);

const How = () => (
  <section className="bg-secondary py-20" data-testid="mini-plan-how">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <h2 className="font-serif uppercase text-3xl text-navy mb-4">How it works</h2>
      <RootsLine className="w-full h-10 text-leaf/50 mb-8" />
      <div className="grid gap-8 md:grid-cols-3">{HOW.map(([t, b], i) => (
        <div key={t}><div className="text-xs font-mono uppercase tracking-[0.2em] text-pinkb">Step {i + 1}</div>
          <h3 className="font-serif uppercase text-xl text-navy mt-2">{t}</h3><p className="text-sm text-foreground/80 mt-2 leading-relaxed">{b}</p></div>
      ))}</div>
    </div>
  </section>
);

const About = () => (
  <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid gap-12 lg:grid-cols-[1fr_1.3fr] items-center" data-testid="mini-plan-about">
    <img src={IMG.lindsay} alt="Lindsay Fenn" className="rounded-3xl aspect-square object-cover object-top w-full max-w-md" />
    <div className="space-y-4 text-foreground/85 leading-relaxed">
      <h2 className="font-serif uppercase text-3xl text-navy">Built by someone who's done it — not taught it.</h2>
      <p>The Roots Method was built by Lindsay Fenn — a former International Marketing Director and brand strategist with over 20 years of hands-on experience across food, drink and hospitality, from challenger startups to established international names.</p>
      <p>She has worked with most major UK retailers, across trade and consumer strategy, and with international hospitality brands in the UK and beyond. She grew Estrella Galicia UK brand awareness by 37% — taking a Spanish beer from specialist restaurants to pub chains and supermarket shelves — and has worked every part of the route to market: buyers, distributors, agencies, founders and everything in between.</p>
      <p>She regularly attends industry events and is part of multiple networks, to make sure the advice in your plan reflects what's actually happening in the market right now — not yesterday's best practice.</p>
      <p className="font-medium text-navy">This isn't a plan built by a template or an algorithm. It's built by a real person who has spent their career doing this. That human expertise — applied specifically to your brand — is the whole point.</p>
    </div>
  </section>
);

const Quotes = () => (
  <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="mini-plan-quotes">
    <h2 className="font-serif uppercase text-3xl text-navy mb-8">What founders say</h2>
    <div className="grid gap-6 md:grid-cols-2">{QUOTES.map(([q, n]) => (
      <figure key={n} className="rounded-3xl bg-card border-2 border-pinkb/20 p-8">
        <blockquote className="text-navy leading-relaxed">“{q}”</blockquote>
        <figcaption className="mt-4 text-sm text-muted-foreground">— {n}</figcaption>
      </figure>
    ))}</div>
  </section>
);

const Offer = () => {
  const [user, price] = useMiniPlanPrice();
  const member = user && price === 59;
  return (
  <section className="mx-auto max-w-3xl px-6 py-20" data-testid="mini-plan-offer-box">
    <div className="rounded-3xl bg-navy text-white p-8 lg:p-12">
      <h2 className="font-serif uppercase text-3xl">Get your Mini Marketing Plan</h2>
      <p className="mt-4"><span className="font-serif text-5xl text-sun" data-testid="offer-price">£{member ? 59 : 99}</span> <span className="text-white/80">— {member ? "your member price (non-members pay £99)" : "one-off price"}. Bespoke to your brand, your stage, and your goals.</span></p>
      <ul className="mt-6 space-y-2">{INCLUDED.map((i) => <li key={i} className="flex gap-2"><Check className="h-5 w-5 text-sun shrink-0" />{i}</li>)}</ul>
      <div className="mt-8"><BuyButtons testId="offer" label="Get Started — £99" /></div>
      {!user && <p className="mt-4 text-sm text-white/70">Already a Roots Club member? <Link to="/login?next=/mini-marketing-plan" className="underline text-sun">Log in</Link> to access your member price of £59.</p>}
    </div>
  </section>
  );
};

const ClubPitch = () => (
  <section className="mx-auto max-w-4xl px-6 pb-8" data-testid="mini-plan-club">
    <div className="rounded-3xl bg-secondary p-8 lg:p-10">
      <h2 className="font-serif uppercase text-2xl text-navy">Want ongoing support beyond your plan?</h2>
      <p className="mt-3 text-foreground/85">Join The Roots Club — Lindsay's members-only platform giving independent food, drink and hospitality founders on-demand marketing expertise, templates, and direct access to Lindsay's knowledge base.</p>
      <p className="mt-3 font-medium text-navy">Members get their Mini Marketing Plan for £59. Membership from £49/month.</p>
      <Link to="/the-roots-club" data-testid="mini-plan-club-link" className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy/90 text-white px-6 py-3 transition-colors">Find out more about The Roots Club <ArrowRight className="h-4 w-4" /></Link>
    </div>
    <p className="mt-10 text-center text-sm text-muted-foreground">Questions? Email <a href={`mailto:${CONTACT}`} className="text-pinkb underline" data-testid="mini-plan-contact">{CONTACT}</a></p>
  </section>
);

export default function MiniPlanPage() {
  return (
    <div className="relative z-10 min-h-screen bg-background" data-testid="mini-plan-page">
      <SiteNav />
      <Hero /><Pain /><WhatYouGet /><How /><About /><Quotes /><Offer /><ClubPitch />
      <SiteFooter email={CONTACT} />
    </div>
  );
}

export const UpsellNote = () => (
  <div className="mt-6 rounded-2xl bg-secondary p-5 text-sm text-left" data-testid="mini-plan-upsell">
    <p className="text-navy font-medium">Want ongoing support? Join The Roots Club from £49/month — and your next Mini Marketing Plan is just £59.</p>
    <Link to="/the-roots-club" className="mt-2 inline-flex items-center gap-1 text-pinkb hover:underline" data-testid="mini-plan-upsell-link">See membership <ArrowRight className="h-3 w-3" /></Link>
  </div>
);

export const QuestionnaireCTA = ({ sessionId }) => {
  const [token, setToken] = useState("");
  useEffect(() => {
    let tries = 0;
    let t;
    const get = () => api.get(`/plan-orders/by-session/${sessionId}`).then((r) => setToken(r.data.token))
      .catch(() => { if (++tries < 10) t = setTimeout(get, 2000); });
    get();
    return () => clearTimeout(t);
  }, [sessionId]);
  return (
    <div className="mt-6 rounded-2xl border-2 border-pinkb/30 bg-card p-5 text-left" data-testid="questionnaire-cta">
      <p className="font-medium text-navy">Next step: your questionnaire</p>
      <p className="text-sm text-muted-foreground mt-1">Most founders take 40 to 60 minutes. Your answers save as you go, and we've emailed you the link too.</p>
      {token ? <Link to={`/plan-questionnaire/${token}`} data-testid="questionnaire-start-link" className="mt-4 inline-flex items-center gap-2 rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-6 py-2.5 text-sm transition-colors">Start my questionnaire <ArrowRight className="h-4 w-4" /></Link>
        : <p className="mt-4 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Getting your questionnaire ready…</p>}
    </div>
  );
};

export function MiniPlanThanks() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  useEffect(() => {
    const id = params.get("session_id");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data.status)) return setState("failed");
      } catch { /* keep polling */ }
      if (++tries < 15) timer = setTimeout(poll, 2000); else setState("failed");
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);
  return (
    <div className="relative z-10 min-h-screen bg-background"><SiteNav />
      <div className="max-w-lg mx-auto text-center px-6 py-24" data-testid={`mini-plan-thanks-${state}`}>
        {state === "checking" && <><Loader2 className="h-12 w-12 animate-spin text-pinkb mx-auto" /><h1 className="text-3xl text-navy mt-6">Confirming your payment…</h1></>}
        {state === "paid" && <><CheckCircle2 className="h-12 w-12 text-leaf mx-auto" /><h1 className="text-3xl text-navy mt-6">Your Mini Marketing Plan is booked.</h1>
          <p className="text-muted-foreground mt-3">Thank you. Your plan arrives as a PDF within 2 working days of receiving your answers.</p><QuestionnaireCTA sessionId={params.get("session_id")} /><UpsellNote /></>}
        {state === "failed" && <><XCircle className="h-12 w-12 text-rose-600 mx-auto" /><h1 className="text-3xl text-navy mt-6">We couldn't confirm that payment.</h1><p className="text-muted-foreground mt-3">If you were charged, it will appear shortly. Otherwise, please try again or email {CONTACT}.</p></>}
      </div>
    </div>
  );
}
